import type { IconProps } from '@carboncopyco/icons';
import classNames from 'classnames';
import React, { forwardRef } from 'react';
import { Spacer } from '../../Spacer';
import { TextButton } from '../../Typography';

export interface ToggleButtonProps {
  onSelect?: (isSelected: boolean) => void;
  selected?: boolean;
  disabled?: boolean;
  leftIcon?: React.ReactElement<IconProps>;
  rightIcon?: React.ReactElement<IconProps>;
  icon?: React.ReactElement<IconProps>;
  warning?: boolean;
  label?: string;
  small?: boolean;
  ref?: React.Ref<HTMLDivElement>;
  dark?: boolean;
  noDrag?: boolean;
  ariaLabel?: string;
}

export const ToggleButton = forwardRef(
  (
    {
      onSelect,
      selected = false,
      disabled,
      leftIcon,
      rightIcon,
      icon,
      warning,
      label,
      small,
      dark,
      noDrag = false,
      ariaLabel,
    }: ToggleButtonProps,
    ref: React.Ref<HTMLDivElement>
  ) => {
    return (
      <div
        aria-label={ariaLabel}
        ref={ref}
        className={classNames(
          'relative',
          disabled ? 'pointer-events-none cursor-default' : 'cursor-pointer'
        )}
        onClick={() => onSelect?.(selected)}
      >
        <input
          readOnly
          disabled={disabled}
          type="checkbox"
          name="toggleButton"
          checked={selected}
          style={{ display: 'none' }}
        />
        <div
          className={classNames(
            'relative flex items-center justify-center max-w-min rounded-md whitespace-nowrap',
            {
              'pl-1.5 pr-1.5': leftIcon || rightIcon,
              'px-2': icon,
              'px-1.5': !icon && !leftIcon && !rightIcon,
              'no-drag': noDrag,
              'pointer-events-none cursor-default': disabled,
              'text-ds-natural-600': !selected && !disabled && !dark,
              'bg-ds-natural-100 text-ds-natural-700': selected && !dark,
              'bg-ds-accent3-100/50': selected && dark,
              'bg-ds-accent3-100/0 text-ds-natural-400': dark && disabled,
              'bg-ds-natural-200/0 text-ds-natural-200': !dark && disabled,
              'hover:bg-ds-natural-50 active:bg-ds-natural-100 active:text-ds-natural-600  w-96':
                !dark,
              'bg-none text-ds-accent3-100 hover:bg-ds-accent3-100/25':
                dark && !disabled,
            },
            small ? 'py-ds-p-5' : 'py-2'
          )}
        >
          {warning && (
            <div
              className={classNames(
                'w-2 h-2 rounded-full absolute right-0 top-0 p-0.5',
                {
                  'bg-ds-secondary': warning,
                }
              )}
            />
          )}
          {icon && React.cloneElement(icon, { size: 20 })}
          {leftIcon && React.cloneElement(leftIcon, { size: 20 })}
          {label && (
            <>
              <Spacer amount={0.5} />
              <TextButton
                label={label}
                color="text-current"
              />
            </>
          )}
          {rightIcon && React.cloneElement(rightIcon, { size: 20 })}
        </div>
      </div>
    );
  }
);

ToggleButton.displayName = 'ToggleButton';
